import trendsService, { type WeeklyReportDay, type WeeklyReportMeal, type WeeklyReportResponse } from "../services/TrendsService";
import { getLocalDateString } from "./homePageUtils";
import { MEDICAL_DISCLAIMER_TEXT } from "./medicalDisclaimer";

type RGB = [number, number, number];
type PillTone = "good" | "caution" | "poor" | "mild" | "moderate" | "high";

const NAVY: RGB = [26, 20, 62];
const PURPLE: RGB = [98, 86, 232];
const LOGO: RGB = [126, 114, 242];
const INK: RGB = [28, 28, 36];
const MUTED: RGB = [120, 124, 136];
const SOFT: RGB = [196, 194, 216];
const LINE: RGB = [228, 230, 236];
const HEAD_BG: RGB = [244, 245, 248];
const WHITE: RGB = [255, 255, 255];
const CALLOUT_BG: RGB = [255, 246, 224];
const CALLOUT_FG: RGB = [138, 96, 24];
const BORDER: RGB = [226, 228, 234];

const PILL: Record<PillTone, { bg: RGB; fg: RGB }> = {
  good: { bg: [226, 246, 232], fg: [28, 148, 78] },
  mild: { bg: [226, 246, 232], fg: [28, 148, 78] },
  caution: { bg: [255, 243, 214], fg: [176, 118, 16] },
  moderate: { bg: [255, 243, 214], fg: [184, 124, 18] },
  poor: { bg: [255, 228, 228], fg: [196, 52, 52] },
  high: { bg: [255, 228, 228], fg: [196, 52, 52] },
};

const SPIKE_COLOR: Record<PillTone, RGB> = {
  good: [28, 148, 78],
  mild: [28, 148, 78],
  caution: [184, 120, 16],
  moderate: [184, 120, 16],
  poor: [196, 48, 48],
  high: [196, 48, 48],
};

const HELVETICA_WIDTHS = [
  278, 278, 355, 556, 556, 889, 667, 191, 333, 333, 389, 584, 278, 333, 278, 278,
  556, 556, 556, 556, 556, 556, 556, 556, 556, 556, 278, 278, 584, 584, 584, 556,
  1015, 667, 667, 722, 722, 667, 611, 778, 722, 278, 500, 667, 556, 833, 722, 778,
  667, 778, 722, 667, 611, 722, 667, 944, 667, 667, 611, 278, 278, 278, 469, 556,
  333, 556, 556, 500, 556, 556, 278, 556, 556, 222, 222, 500, 222, 833, 556, 556,
  556, 556, 333, 500, 278, 556, 500, 722, 500, 500, 500, 334, 260, 334, 584,
];

function shiftLocalDate(dateStr: string, deltaDays: number): string {
  const date = new Date(`${dateStr}T12:00:00`);
  date.setDate(date.getDate() + deltaDays);
  return getLocalDateString(date);
}

function formatLong(value: string): string {
  return new Date(`${value}T12:00:00`).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function formatDayMonth(value: string): string {
  return new Date(`${value}T12:00:00`).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
  });
}

function weekdayShort(value: string): string {
  return new Date(`${value}T12:00:00`).toLocaleDateString("en-US", { weekday: "short" });
}

function scoreRating(score: number): { label: string; tone: PillTone } {
  if (score >= 60) return { label: "Good", tone: "good" };
  if (score >= 40) return { label: "Caution", tone: "caution" };
  return { label: "Poor", tone: "poor" };
}

function spikeTone(spike: number | null | undefined): { label: string; tone: PillTone } | null {
  if (typeof spike !== "number" || !Number.isFinite(spike)) return null;
  if (spike >= 60) return { label: "High", tone: "high" };
  if (spike >= 40) return { label: "Moderate", tone: "moderate" };
  return { label: "Mild", tone: "mild" };
}

function pdfPlain(value: string): string {
  return value.replace(/[^\x20-\x7E]/g, " ").replace(/\s+/g, " ").trim();
}

function pdfEscape(value: string): string {
  return value.replace(/\\/g, "\\\\").replace(/\(/g, "\\(").replace(/\)/g, "\\)");
}

function textWidth(value: string, size: number, bold = false): number {
  let units = 0;
  for (const ch of value) {
    const code = ch.charCodeAt(0);
    units += code >= 32 && code <= 126 ? HELVETICA_WIDTHS[code - 32] : 500;
  }
  return (units / 1000) * size * (bold ? 1.06 : 1);
}

function wrapText(value: string, size: number, maxWidth: number, bold: boolean): string[] {
  const words = pdfPlain(value).split(" ").filter(Boolean);
  if (words.length === 0) return [];
  const lines: string[] = [];
  let current = "";
  const pushLong = (word: string) => {
    let chunk = "";
    for (const ch of word) {
      const trial = chunk + ch;
      if (textWidth(trial, size, bold) <= maxWidth) chunk = trial;
      else {
        if (chunk) lines.push(chunk);
        chunk = ch;
      }
    }
    current = chunk;
  };
  words.forEach((word) => {
    const next = current ? `${current} ${word}` : word;
    if (textWidth(next, size, bold) <= maxWidth) {
      current = next;
      return;
    }
    if (current) lines.push(current);
    if (textWidth(word, size, bold) <= maxWidth) current = word;
    else pushLong(word);
  });
  if (current) lines.push(current);
  return lines;
}

function optionalNumber(source: object, keys: string[]): number | null {
  const row = source as Record<string, unknown>;
  for (const key of keys) {
    const value = row[key];
    if (typeof value === "number" && Number.isFinite(value)) return value;
    if (value && typeof value === "object") {
      const nested = value as Record<string, unknown>;
      const inner = nested.value ?? nested.weight_kg ?? nested.kg;
      if (typeof inner === "number" && Number.isFinite(inner)) return inner;
    }
  }
  return null;
}

function readMetric(report: WeeklyReportResponse, days: WeeklyReportDay[], keys: string[]): number | null {
  const pick = (value: number | null) => (value != null && value > 0 ? value : null);
  const fromDays = [...days].reverse().map((day) => pick(optionalNumber(day, keys))).find((value) => value != null);
  if (fromDays != null) return fromDays;
  const root = pick(optionalNumber(report, keys));
  if (root != null) return root;
  const record = report as Record<string, unknown>;
  for (const key of ["body", "vitals", "snapshot", "body_snapshot", "latest"]) {
    const nested = record[key];
    if (nested && typeof nested === "object") {
      const value = pick(optionalNumber(nested, keys));
      if (value != null) return value;
    }
  }
  return null;
}

function foodNames(meal: WeeklyReportMeal): string {
  return (meal.food_names ?? []).map((name) => name.trim()).filter(Boolean).join(", ");
}

function formatQuantity(value: number): string {
  const rounded = Math.round(value * 10) / 10;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
}

function readingText(reading: Record<string, unknown>): string {
  const pick = (...keys: string[]) => {
    for (const key of keys) {
      const value = reading[key];
      if (typeof value === "string" && value.trim()) return value.trim();
      if (typeof value === "number" && Number.isFinite(value)) return String(value);
    }
    return "";
  };
  const label = (pick("reading_type", "type", "label", "name") || "Reading").replace(/_/g, " ");
  const value = pick("value", "mg_dl", "reading", "glucose_mg_dl", "hba1c");
  const unit = pick("unit") || (value ? "mg/dL" : "");
  const when = pick("time", "entered_at", "logged_at");
  return [label, value ? `${value}${unit ? ` ${unit}` : ""}` : "", when].filter(Boolean).join(" - ");
}

interface ScoreRow {
  date: string;
  score: string;
  rating: string;
  tone: PillTone | null;
}

interface MealRow {
  date: string;
  meal: string;
  name: string;
  foods: string;
  spike: string;
  level: string;
  tone: PillTone | null;
}

interface RankRow {
  rank: string;
  name: string;
  when: string;
  spike: string;
  tone: PillTone;
}

interface ReportView {
  period: string;
  generated: string;
  footer: string;
  summary: { label: string; value: string }[];
  scores: ScoreRow[];
  meals: MealRow[];
  ranks: RankRow[];
  activity: { measure: string; value: string; note: string }[];
  activityNote: string;
  body: { measure: string; value: string; recorded: boolean }[];
  callout: string;
  disclaimer: string;
}

function buildView(days: WeeklyReportDay[], report: WeeklyReportResponse, start: string, end: string): ReportView {
  const meals = days.flatMap((day) => (day.meals ?? []).map((meal) => ({ day, meal })));
  const scored = days.filter((day) => typeof day.bite_balance_score === "number");
  const avgScore = scored.length
    ? Math.round(scored.reduce((sum, day) => sum + (day.bite_balance_score ?? 0), 0) / scored.length)
    : null;
  const daysWithMeals = days.filter((day) => (day.meals ?? []).length > 0).length;
  const spikes = meals.filter((item) => typeof item.meal.spike_mg_dl === "number");
  const avgSpike = spikes.length
    ? Math.round(spikes.reduce((sum, item) => sum + (item.meal.spike_mg_dl ?? 0), 0) / spikes.length)
    : null;
  const highest = [...spikes].sort((a, b) => (b.meal.spike_mg_dl ?? 0) - (a.meal.spike_mg_dl ?? 0))[0];
  const readings = days.flatMap((day) =>
    (day.blood_sugar_readings ?? []).map((reading) => ({ date: day.date, text: readingText(reading) })),
  );
  const avgSteps = days.length ? Math.round(days.reduce((sum, day) => sum + (day.steps || 0), 0) / days.length) : 0;
  const avgCalories = days.length
    ? Math.round(days.reduce((sum, day) => sum + (day.calories_burned || 0), 0) / days.length)
    : 0;
  const sleepMinutes = days
    .map((day) => day.sleep_minutes)
    .filter((value): value is number => typeof value === "number" && Number.isFinite(value));
  const roundedSleep = sleepMinutes.length
    ? Math.round(sleepMinutes.reduce((sum, value) => sum + value, 0) / sleepMinutes.length)
    : 0;
  const sleepLabel = sleepMinutes.length
    ? `${Math.floor(roundedSleep / 60)}h ${roundedSleep % 60}m`
    : days[0]?.sleep || "0h 0m";
  const noActivity = avgSteps === 0 && avgCalories === 0;

  const weight = readMetric(report, days, ["weight_kg", "weight", "latest_weight_kg"]);
  const hrv = readMetric(report, days, ["hrv_rmssd", "hrv", "heart_rate_variability"]);
  const spo2 = readMetric(report, days, ["spo2_percent", "spo2", "spo2_percentage"]);
  const breathing = readMetric(report, days, ["breathing_rate", "respiratory_rate"]);
  const metric = (value: number | null, suffix: string) =>
    value == null ? { value: "Not recorded", recorded: false } : { value: `${formatQuantity(value)} ${suffix}`.trim(), recorded: true };

  const scores: ScoreRow[] = [];
  let emptyStart: WeeklyReportDay | null = null;
  let emptyEnd: WeeklyReportDay | null = null;
  const flushEmpty = () => {
    if (!emptyStart) return;
    const endDay = emptyEnd ?? emptyStart;
    const startLabel = formatDayMonth(emptyStart.date);
    const endLabel = formatDayMonth(endDay.date);
    const sameMonth = emptyStart.date.slice(0, 7) === endDay.date.slice(0, 7);
    const label = emptyStart.date === endDay.date
      ? startLabel
      : sameMonth
        ? `${startLabel.split(" ")[0]} - ${endLabel}`
        : `${startLabel} - ${endLabel}`;
    scores.push({ date: label, score: "-", rating: "No meals logged", tone: null });
    emptyStart = null;
    emptyEnd = null;
  };
  days.forEach((day) => {
    const hasScore = typeof day.bite_balance_score === "number";
    const hasMeals = (day.meals ?? []).length > 0;
    if (!hasScore && !hasMeals) {
      if (!emptyStart) emptyStart = day;
      emptyEnd = day;
      return;
    }
    flushEmpty();
    if (!hasScore) {
      scores.push({
        date: `${weekdayShort(day.date)} ${new Date(`${day.date}T12:00:00`).getDate()}`,
        score: "-",
        rating: "-",
        tone: null,
      });
      return;
    }
    const rating = scoreRating(Math.round(day.bite_balance_score ?? 0));
    scores.push({
      date: `${weekdayShort(day.date)} ${new Date(`${day.date}T12:00:00`).getDate()}`,
      score: String(Math.round(day.bite_balance_score ?? 0)),
      rating: rating.label,
      tone: rating.tone,
    });
  });
  flushEmpty();

  const ranked = new Map<string, { name: string; when: string; date: string; spike: number; tone: PillTone }>();
  spikes.forEach((item) => {
    const name = item.meal.meal_name?.trim() || foodNames(item.meal) || "Meal";
    const spike = item.meal.spike_mg_dl ?? 0;
    const tone = spikeTone(spike)?.tone ?? "mild";
    const key = name.toLowerCase();
    const prev = ranked.get(key);
    if (!prev || spike > prev.spike || (spike === prev.spike && item.day.date < prev.date)) {
      ranked.set(key, { name, when: formatDayMonth(item.day.date), date: item.day.date, spike, tone });
    }
  });

  const weightRow = metric(weight, "kg");
  const hrvRow = metric(hrv, "ms");
  const spo2Row = spo2 == null
    ? { value: "Not recorded", recorded: false }
    : { value: `${Math.round(spo2)}%`, recorded: true };
  const breathingRow = metric(breathing, "breaths/min");

  const callout = readings.length === 0
    ? "No blood sugar readings were entered this week. No finger-prick, fasting, CGM or HbA1c value was logged. The spike values in this report are app estimates, not measured glucose."
    : [
        `${readings.length} blood sugar reading${readings.length === 1 ? "" : "s"} entered this week.`,
        ...readings.map((reading) => `${formatDayMonth(reading.date)}  ${reading.text}`),
        "Spike values in this report are app estimates, not measured glucose.",
      ].join(" ");

  return {
    period: `Period: ${formatLong(start)} - ${formatLong(end)} (${days.length || 7} days)`,
    generated: `Generated ${formatLong(end)}`,
    footer: `Bite Balance weekly report | ${formatDayMonth(start)} - ${formatDayMonth(end)} ${end.slice(0, 4)}`,
    summary: [
      { label: "Average Bite Balance Score", value: avgScore == null ? "No score logged" : `${avgScore} out of 100` },
      { label: "Meals logged", value: `${meals.length} meal${meals.length === 1 ? "" : "s"} on ${daysWithMeals} of ${days.length || 7} days` },
      { label: "Average glucose spike per meal", value: avgSpike == null ? "None" : `+${avgSpike} mg/dL` },
      {
        label: "Highest spike",
        value: highest
          ? `+${Math.round(highest.meal.spike_mg_dl ?? 0)} mg/dL - ${highest.meal.meal_name?.trim() || "Meal"} (${formatDayMonth(highest.day.date)})`
          : "None this week",
      },
      { label: "Blood sugar readings entered", value: readings.length === 0 ? "None this week" : String(readings.length) },
    ],
    scores,
    meals: meals.map(({ day, meal }) => {
      const tone = spikeTone(meal.spike_mg_dl);
      const name = meal.meal_name?.trim() || "Meal";
      return {
        date: formatDayMonth(day.date),
        meal: meal.meal_type || "Meal",
        name,
        foods: foodNames(meal) || name,
        spike: tone ? `+${Math.round(meal.spike_mg_dl ?? 0)} mg/dL` : "-",
        level: tone?.label ?? "-",
        tone: tone?.tone ?? null,
      };
    }),
    ranks: [...ranked.values()]
      .sort((a, b) => b.spike - a.spike)
      .map((item, index) => ({
        rank: String(index + 1),
        name: item.name,
        when: item.when,
        spike: `+${Math.round(item.spike)} mg/dL`,
        tone: item.tone,
      })),
    activity: [
      { measure: "Steps per day (average)", value: avgSteps.toLocaleString("en-US"), note: "Goal: 10,000 per day" },
      { measure: "Calories burned", value: `${avgCalories.toLocaleString("en-US")} kcal`, note: "Goal: 1,000 kcal per day" },
      { measure: "Sleep", value: sleepLabel, note: noActivity || roundedSleep === 0 ? "No data synced" : "Logged this week" },
    ],
    activityNote: noActivity
      ? "Zeros usually mean no wearable or health app was connected, not that the person was inactive."
      : "",
    body: [
      { measure: "Weight", ...weightRow },
      { measure: "Heart rate variability", ...hrvRow },
      { measure: "SpO2", ...spo2Row },
      { measure: "Breathing rate", ...breathingRow },
    ],
    callout,
    disclaimer: `Disclaimer. This report records what the Bite Balance app logged. It is not a diagnosis. ${MEDICAL_DISCLAIMER_TEXT}`,
  };
}

function rgb(color: RGB): string {
  return `${(color[0] / 255).toFixed(3)} ${(color[1] / 255).toFixed(3)} ${(color[2] / 255).toFixed(3)}`;
}

function num(value: number): string {
  return value.toFixed(2);
}

function roundedRect(x: number, y: number, w: number, h: number, r: number): string {
  const radius = Math.max(0, Math.min(r, w / 2, h / 2));
  const k = 0.5522847498 * radius;
  return [
    `${num(x + radius)} ${num(y)} m`,
    `${num(x + w - radius)} ${num(y)} l`,
    `${num(x + w - radius + k)} ${num(y)} ${num(x + w)} ${num(y + radius - k)} ${num(x + w)} ${num(y + radius)} c`,
    `${num(x + w)} ${num(y + h - radius)} l`,
    `${num(x + w)} ${num(y + h - radius + k)} ${num(x + w - radius + k)} ${num(y + h)} ${num(x + w - radius)} ${num(y + h)} c`,
    `${num(x + radius)} ${num(y + h)} l`,
    `${num(x + radius - k)} ${num(y + h)} ${num(x)} ${num(y + h - radius + k)} ${num(x)} ${num(y + h - radius)} c`,
    `${num(x)} ${num(y + radius)} l`,
    `${num(x)} ${num(y + radius - k)} ${num(x + radius - k)} ${num(y)} ${num(x + radius)} ${num(y)} c`,
    "h",
  ].join("\n");
}

class PdfPages {
  private pages: string[][] = [[]];
  private top = 28;
  readonly pageWidth = 595;
  readonly pageHeight = 842;
  readonly margin = 28;

  get width(): number {
    return this.pageWidth - this.margin * 2;
  }

  get x(): number {
    return this.margin;
  }

  get y(): number {
    return this.top;
  }

  private add(command: string): void {
    this.pages[this.pages.length - 1].push(command);
  }

  private breakPage(): void {
    this.pages.push([]);
    this.top = 32;
  }

  need(height: number): void {
    if (this.top + height > this.pageHeight - 48 && this.top > this.margin + 4) this.breakPage();
  }

  advance(height: number): void {
    this.top += height;
  }

  fillRound(x: number, top: number, w: number, h: number, radius: number, color: RGB): void {
    const y = this.pageHeight - top - h;
    this.add(`${rgb(color)} rg`);
    this.add(roundedRect(x, y, w, h, radius));
    this.add("f");
  }

  strokeRound(x: number, top: number, w: number, h: number, radius: number, color: RGB): void {
    const y = this.pageHeight - top - h;
    this.add(`${rgb(color)} RG`);
    this.add("0.8 w");
    this.add(roundedRect(x, y, w, h, radius));
    this.add("S");
  }

  circle(cx: number, centerFromTop: number, radius: number, color: RGB): void {
    const cy = this.pageHeight - centerFromTop;
    const k = 0.5522847498 * radius;
    this.add(`${rgb(color)} rg`);
    this.add(
      [
        `${num(cx)} ${num(cy + radius)} m`,
        `${num(cx + k)} ${num(cy + radius)} ${num(cx + radius)} ${num(cy + k)} ${num(cx + radius)} ${num(cy)} c`,
        `${num(cx + radius)} ${num(cy - k)} ${num(cx + k)} ${num(cy - radius)} ${num(cx)} ${num(cy - radius)} c`,
        `${num(cx - k)} ${num(cy - radius)} ${num(cx - radius)} ${num(cy - k)} ${num(cx - radius)} ${num(cy)} c`,
        `${num(cx - radius)} ${num(cy + k)} ${num(cx - k)} ${num(cy + radius)} ${num(cx)} ${num(cy + radius)} c`,
        "f",
      ].join("\n"),
    );
  }

  hline(x: number, top: number, w: number): void {
    const y = this.pageHeight - top;
    this.add(`${rgb(LINE)} RG`);
    this.add("0.6 w");
    this.add(`${num(x)} ${num(y)} m ${num(x + w)} ${num(y)} l S`);
  }

  text(value: string, x: number, baselineFromTop: number, size: number, color: RGB, bold = false): void {
    const plain = pdfPlain(value);
    if (!plain) return;
    const y = this.pageHeight - baselineFromTop;
    this.add("BT");
    this.add(`/${bold ? "F2" : "F1"} ${size} Tf`);
    this.add(`${rgb(color)} rg`);
    this.add(`1 0 0 1 ${num(x)} ${num(y)} Tm`);
    this.add(`(${pdfEscape(plain)}) Tj`);
    this.add("ET");
  }

  textRight(value: string, right: number, baselineFromTop: number, size: number, color: RGB, bold = false): void {
    const plain = pdfPlain(value);
    this.text(plain, right - textWidth(plain, size, bold), baselineFromTop, size, color, bold);
  }

  private rowBaseline(top: number, height: number, size: number): number {
    return top + height / 2 + size * 0.32;
  }

  header(period: string, generated: string): void {
    const top = this.top;
    const height = 102;
    this.fillRound(this.x, top, this.width, height, 14, NAVY);
    const cx = this.x + 34;
    const cy = top + 40;
    this.circle(cx, cy, 15, LOGO);
    const mark = "BB";
    this.text(mark, cx - textWidth(mark, 8, true) / 2, cy + 2.6, 8, WHITE, true);
    this.text("BITE BALANCE", this.x + 58, top + 30, 8, SOFT, true);
    this.text("Weekly Health Report", this.x + 58, top + 52, 18, WHITE, true);
    this.text(period, this.x + 58, top + 74, 9, SOFT, false);
    this.textRight(generated, this.x + this.width - 16, top + 36, 8, SOFT, false);
    this.textRight("For clinician review", this.x + this.width - 16, top + 50, 8, SOFT, false);
    this.top = top + height + 18;
  }

  section(num: string, title: string, aside: string): void {
    this.need(86);
    const top = this.top;
    this.fillRound(this.x, top, 18, 18, 5, PURPLE);
    const label = String(num);
    this.text(label, this.x + (18 - textWidth(label, 9, true)) / 2, top + 13.2, 9, WHITE, true);
    this.text(title, this.x + 26, top + 13.4, 12, INK, true);
    if (aside) this.textRight(aside, this.x + this.width, top + 13, 8, MUTED, false);
    this.top = top + 28;
  }

  summary(rows: { label: string; value: string }[]): void {
    rows.forEach((row) => {
      this.need(24);
      const top = this.top;
      const baseline = this.rowBaseline(top, 22, 9);
      this.text(row.label, this.x, baseline, 9, INK, false);
      const maxValue = this.width - textWidth(pdfPlain(row.label), 9, false) - 18;
      this.textRight(clipText(row.value, 9, maxValue, true), this.x + this.width, baseline, 9, INK, true);
      this.hline(this.x, top + 22, this.width);
      this.top = top + 22;
    });
    this.advance(14);
  }

  private columns(weights: number[], labels: string[], align: Array<"left" | "right">): Array<{ label: string; width: number; align: "left" | "right" }> {
    const total = weights.reduce((sum, weight) => sum + weight, 0);
    return weights.map((weight, index) => ({
      label: labels[index],
      width: (weight / total) * this.width,
      align: align[index],
    }));
  }

  private tableHead(cols: Array<{ label: string; width: number; align: "left" | "right" }>): void {
    this.need(42);
    const top = this.top;
    this.fillRound(this.x, top, this.width, 20, 4, HEAD_BG);
    let x = this.x;
    const baseline = this.rowBaseline(top, 20, 7);
    cols.forEach((col) => {
      const label = col.label.toUpperCase();
      if (col.align === "right") this.textRight(label, x + col.width - 8, baseline, 7, MUTED, true);
      else this.text(label, x + 8, baseline, 7, MUTED, true);
      x += col.width;
    });
    this.top = top + 20;
  }

  scoreTable(rows: ScoreRow[]): void {
    const cols = this.columns([34, 22, 44], ["Date", "Score", "Rating"], ["left", "left", "left"]);
    this.tableHead(cols);
    if (rows.length === 0) {
      this.emptyRow("No meals were logged this week.");
      return;
    }
    rows.forEach((row) => {
      this.need(26);
      const top = this.top;
      const height = 24;
      const baseline = this.rowBaseline(top, height, 9);
      let x = this.x;
      this.text(row.date, x + 8, baseline, 9, INK, false);
      x += cols[0].width;
      this.text(row.score, x + 8, baseline, 9, INK, row.score !== "-");
      x += cols[1].width;
      if (row.tone) this.pill(row.rating, x + 8, top, height, row.tone, "left");
      else this.text(row.rating, x + 8, baseline, 9, MUTED, false);
      this.hline(this.x, top + height, this.width);
      this.top = top + height;
    });
    this.advance(14);
  }

  mealTable(rows: MealRow[]): void {
    const cols = this.columns([16, 16, 34, 18, 16], ["Date", "Meal", "Food", "Spike", "Level"], ["left", "left", "left", "right", "right"]);
    this.tableHead(cols);
    if (rows.length === 0) {
      this.emptyRow("No meals were logged this week.");
      this.legend();
      return;
    }
    rows.forEach((row) => {
      const foodWidth = cols[2].width - 16;
      const nameLines = wrapText(row.name, 9, foodWidth, true).slice(0, 2);
      const foodLines = wrapText(row.foods, 8, foodWidth, false).slice(0, 2);
      const height = Math.max(34, 8 + nameLines.length * 11 + foodLines.length * 10 + 6);
      this.need(height + 2);
      const top = this.top;
      const mid = this.rowBaseline(top, height, 9);
      let x = this.x;
      this.text(row.date, x + 8, mid, 9, INK, false);
      x += cols[0].width;
      this.text(row.meal, x + 8, mid, 9, INK, false);
      x += cols[1].width;
      nameLines.forEach((line, index) => this.text(line, x + 8, top + 16 + index * 11, 9, INK, true));
      foodLines.forEach((line, index) => this.text(line, x + 8, top + 16 + nameLines.length * 11 + index * 10, 8, MUTED, false));
      x += cols[2].width;
      const spikeRight = x + cols[3].width - 8;
      this.textRight(row.spike, spikeRight, mid, 9, row.tone ? SPIKE_COLOR[row.tone] : MUTED, true);
      x += cols[3].width;
      if (row.tone) this.pill(row.level, x + cols[4].width - 8, top, height, row.tone, "right");
      else this.textRight(row.level, x + cols[4].width - 8, mid, 9, MUTED, false);
      this.hline(this.x, top + height, this.width);
      this.top = top + height;
    });
    this.legend();
  }

  private legend(): void {
    this.advance(8);
    this.need(16);
    this.text("Level: Mild below +40 | Moderate +40 to +59 | High +60 and above.", this.x, this.top + 8, 8, MUTED, false);
    this.advance(20);
  }

  rankTable(rows: RankRow[]): void {
    const cols = this.columns([12, 60, 28], ["Rank", "Food", "Spike"], ["left", "left", "right"]);
    this.tableHead(cols);
    if (rows.length === 0) {
      this.emptyRow("No spike was estimated this week.");
      return;
    }
    rows.forEach((row) => {
      this.need(26);
      const top = this.top;
      const height = 24;
      const baseline = this.rowBaseline(top, height, 9);
      let x = this.x;
      this.text(row.rank, x + 8, baseline, 9, INK, false);
      x += cols[0].width;
      const whenLabel = ` (${row.when})`;
      const name = clipText(row.name, 9, Math.max(48, cols[1].width - 16 - textWidth(whenLabel, 9, false)), true);
      this.text(name, x + 8, baseline, 9, INK, true);
      this.text(whenLabel, x + 8 + textWidth(pdfPlain(name), 9, true), baseline, 9, MUTED, false);
      x += cols[1].width;
      this.textRight(row.spike, x + cols[2].width - 8, baseline, 9, SPIKE_COLOR[row.tone], true);
      this.hline(this.x, top + height, this.width);
      this.top = top + height;
    });
    this.advance(16);
  }

  simpleTable(headers: string[], weights: number[], rows: Array<{ cells: Array<{ text: string; bold?: boolean; color?: RGB }> }>, note = ""): void {
    const cols = this.columns(weights, headers, headers.map(() => "left"));
    this.tableHead(cols);
    rows.forEach((row) => {
      this.need(26);
      const top = this.top;
      const height = 24;
      const baseline = this.rowBaseline(top, height, 9);
      let x = this.x;
      row.cells.forEach((cell, index) => {
        this.text(cell.text, x + 8, baseline, 9, cell.color ?? INK, Boolean(cell.bold));
        x += cols[index].width;
      });
      this.hline(this.x, top + height, this.width);
      this.top = top + height;
    });
    if (note) {
      this.advance(8);
      const lines = wrapText(note, 8, this.width, false);
      lines.forEach((line) => {
        this.need(12);
        this.text(line, this.x, this.top + 8, 8, MUTED, false);
        this.advance(11);
      });
      this.advance(8);
    } else {
      this.advance(16);
    }
  }

  private emptyRow(message: string): void {
    this.need(26);
    const top = this.top;
    this.text(message, this.x + 8, this.rowBaseline(top, 24, 9), 9, MUTED, false);
    this.hline(this.x, top + 24, this.width);
    this.top = top + 24;
    this.advance(14);
  }

  private pill(label: string, anchor: number, rowTop: number, rowHeight: number, tone: PillTone, align: "left" | "right"): void {
    const size = 8;
    const textW = textWidth(pdfPlain(label), size, true);
    const width = textW + 14;
    const height = 14;
    const x = align === "right" ? anchor - width : anchor;
    const top = rowTop + (rowHeight - height) / 2;
    const colors = PILL[tone];
    this.fillRound(x, top, width, height, 7, colors.bg);
    this.text(label, x + 7, top + 10.4, size, colors.fg, true);
  }

  callout(message: string): void {
    const lines = wrapText(message, 8.5, this.width - 24, false);
    const height = Math.max(36, lines.length * 12 + 16);
    this.need(height + 8);
    const top = this.top;
    this.fillRound(this.x, top, this.width, height, 8, CALLOUT_BG);
    lines.forEach((line, index) => this.text(line, this.x + 12, top + 16 + index * 12, 8.5, CALLOUT_FG, false));
    this.top = top + height + 14;
  }

  notes(disclaimer: string): void {
    const lines = wrapText(disclaimer, 8, this.width, false);
    const box = 78;
    this.need(box + lines.length * 11 + 12);
    const top = this.top;
    this.strokeRound(this.x, top, this.width, box, 8, BORDER);
    this.text("Clinician notes", this.x + 14, top + 22, 11, INK, true);
    this.top = top + box + 10;
    lines.forEach((line) => {
      this.text(line, this.x, this.top + 8, 8, MUTED, false);
      this.advance(11);
    });
  }

  build(footerLabel: string): string[] {
    return this.pages.map((commands, index) => {
      const right = `Page ${index + 1}`;
      const rightX = this.pageWidth - this.margin - textWidth(right, 8, false);
      const footer = [
        `${rgb(LINE)} RG`,
        "0.6 w",
        `${num(this.margin)} 40 m ${num(this.pageWidth - this.margin)} 40 l S`,
        `BT /F1 8 Tf ${rgb(MUTED)} rg 1 0 0 1 ${num(this.margin)} 26 Tm (${pdfEscape(pdfPlain(footerLabel))}) Tj ET`,
        `BT /F1 8 Tf ${rgb(MUTED)} rg 1 0 0 1 ${num(rightX)} 26 Tm (${right}) Tj ET`,
      ].join("\n");
      return `${commands.join("\n")}\n${footer}`;
    });
  }
}

function clipText(value: string, size: number, maxWidth: number, bold: boolean): string {
  const plain = pdfPlain(value);
  if (textWidth(plain, size, bold) <= maxWidth) return plain;
  let out = plain;
  while (out.length > 1 && textWidth(`${out}...`, size, bold) > maxWidth) out = out.slice(0, -1);
  return `${out.trimEnd()}...`;
}

function paintReport(view: ReportView): string[] {
  const doc = new PdfPages();
  doc.header(view.period, view.generated);
  doc.section("1", "Summary", "This week at a glance");
  doc.summary(view.summary);
  doc.section("2", "Daily score", "Good 60+ | Caution 40-59 | Poor below 40");
  doc.scoreTable(view.scores);
  doc.section("3", "Meals and glucose spike", "Spike = estimated rise after eating");
  doc.mealTable(view.meals);
  doc.section("4", "Top foods by spike", "Highest first");
  doc.rankTable(view.ranks);
  doc.section("5", "Activity and sleep", "Same for all 7 days");
  doc.simpleTable(
    ["Measure", "This week", "Note"],
    [42, 22, 36],
    view.activity.map((row) => ({
      cells: [
        { text: row.measure },
        { text: row.value, bold: true },
        { text: row.note, color: MUTED },
      ],
    })),
    view.activityNote,
  );
  doc.section("6", "Body snapshot", "Latest values");
  doc.simpleTable(
    ["Measure", "Value"],
    [58, 42],
    view.body.map((row) => ({
      cells: [
        { text: row.measure },
        { text: row.value, bold: row.recorded, color: row.recorded ? INK : MUTED },
      ],
    })),
  );
  doc.callout(view.callout);
  doc.notes(view.disclaimer);
  return doc.build(view.footer);
}

function buildPdf(pages: string[]): string {
  const objects: string[] = [];
  objects[1] = "<< /Type /Catalog /Pages 2 0 R >>";
  objects[3] = "<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>";
  objects[4] = "<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold >>";
  const pageIds: number[] = [];
  let nextId = 5;
  pages.forEach((stream) => {
    const contentId = nextId;
    const pageId = nextId + 1;
    nextId += 2;
    pageIds.push(pageId);
    const body = stream.endsWith("\n") ? stream : `${stream}\n`;
    objects[contentId] = `<< /Length ${body.length} >>\nstream\n${body}endstream`;
    objects[pageId] =
      `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 595 842] /Contents ${contentId} 0 R /Resources << /Font << /F1 3 0 R /F2 4 0 R >> >> >>`;
  });
  objects[2] = `<< /Type /Pages /Count ${pages.length} /Kids [${pageIds.map((id) => `${id} 0 R`).join(" ")}] >>`;

  let pdf = "%PDF-1.4\n";
  const offsets = [0];
  for (let id = 1; id < objects.length; id += 1) {
    if (!objects[id]) continue;
    offsets[id] = pdf.length;
    pdf += `${id} 0 obj\n${objects[id]}\nendobj\n`;
  }
  const xref = pdf.length;
  const maxId = objects.length - 1;
  pdf += `xref\n0 ${maxId + 1}\n`;
  pdf += "0000000000 65535 f \n";
  for (let id = 1; id <= maxId; id += 1) {
    pdf += `${String(offsets[id] || 0).padStart(10, "0")} 00000 n \n`;
  }
  pdf += `trailer\n<< /Size ${maxId + 1} /Root 1 0 R >>\nstartxref\n${xref}\n%%EOF`;
  return pdf;
}

function weekHasLoggedData(days: WeeklyReportDay[], report: WeeklyReportResponse): boolean {
  const hasMeals = days.some((day) => (day.meals ?? []).length > 0);
  const hasScore = days.some((day) => typeof day.bite_balance_score === "number");
  const hasReadings = days.some((day) => (day.blood_sugar_readings ?? []).length > 0);
  const hasActivity = days.some(
    (day) => (day.steps || 0) > 0 || (day.calories_burned || 0) > 0 || (day.sleep_minutes || 0) > 0,
  );
  const hasBody = [
    ["weight_kg", "weight", "latest_weight_kg"],
    ["hrv_rmssd", "hrv", "heart_rate_variability"],
    ["spo2_percent", "spo2", "spo2_percentage"],
    ["breathing_rate", "respiratory_rate"],
  ].some((keys) => readMetric(report, days, keys) != null);
  return hasMeals || hasScore || hasReadings || hasActivity || hasBody;
}

export async function downloadWeeklyReport(endDate: string): Promise<{ empty: boolean }> {
  const start = shiftLocalDate(endDate, -6);
  const report = await trendsService.getWeeklyReport(start, endDate);
  const days = report?.days ?? [];
  if (!report || days.length === 0) {
    throw new Error("Weekly report was empty");
  }
  const rangeStart = report.start_date || start;
  const rangeEnd = report.end_date || endDate;
  const empty = !weekHasLoggedData(days, report);
  const pdf = buildPdf(paintReport(buildView(days, report, rangeStart, rangeEnd)));
  const blob = new Blob([pdf], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `bite-balance-weekly-report-${rangeStart}-to-${rangeEnd}.pdf`;
  link.click();
  URL.revokeObjectURL(url);
  return { empty };
}
